/**
 * Synthesizes the analyzer's emotion verdict into a one-line "read": a state
 * label ("Highly emotional"), an intensity %, and a human phrasing
 * ("Fear-driven, with anger underneath"). Powers the EmotionalRead lead band.
 *
 * Every analyzer field is optional, so each output is independently nullable
 * and the caller gates on what's present.
 */

export interface EmotionDetectionLike {
  primary_emotion?: string | null;
  secondary_emotion?: string | null;
  emotional_state?: string | null;
  emotional_intensity?: number | null;
}

export type EmotionalTone = 'alert' | 'warn' | 'calm';

export interface EmotionalRead {
  stateLabel: string;
  stateTone: EmotionalTone;
  intensityPct: number | null;
  phrasing: string | null;
}

// Known analyzer `emotional_state` vocab → display label + tone. Unknown states
// fall back to a title-cased value so new analyzer vocab never crashes the UI.
const STATE_MAP: Record<string, { label: string; tone: EmotionalTone }> = {
  highly_emotional: { label: 'Highly emotional', tone: 'alert' },
  emotional: { label: 'Emotional', tone: 'warn' },
  moderately_emotional: { label: 'Emotional', tone: 'warn' },
  neutral: { label: 'Measured', tone: 'calm' },
  calm: { label: 'Measured', tone: 'calm' },
};

function titleCase(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function deriveEmotionalRead(
  emotion: EmotionDetectionLike | null | undefined,
): EmotionalRead | null {
  if (!emotion) return null;

  const primary = emotion.primary_emotion?.trim() || '';
  const state = emotion.emotional_state?.trim() || '';
  // Nothing to say without at least a state or a primary emotion.
  if (!primary && !state) return null;

  const mapped = state ? STATE_MAP[state.toLowerCase()] : undefined;
  const stateLabel = mapped?.label ?? (state ? titleCase(state) : 'Emotional');
  const stateTone: EmotionalTone = mapped?.tone ?? 'warn';

  // Contract is 0–1, but clamp so an out-of-range analyzer value can't overflow
  // the meter width or print a bad percentage.
  const intensityPct =
    typeof emotion.emotional_intensity === 'number'
      ? Math.max(
          0,
          Math.min(100, Math.round(emotion.emotional_intensity * 100)),
        )
      : null;

  let phrasing: string | null = null;
  if (primary) {
    const secondary = emotion.secondary_emotion?.trim() || '';
    phrasing =
      secondary && secondary.toLowerCase() !== primary.toLowerCase()
        ? `${titleCase(primary)}-driven, with ${secondary.toLowerCase()} underneath`
        : `${titleCase(primary)}-driven`;
  }

  return { stateLabel, stateTone, intensityPct, phrasing };
}
